import {createPromotion, updatePromotion} from '@/services/PromotionsService/promotions'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {PromotionStatus, SegmentType} from '@/types/promotion'
import {ROUTES} from '@/utils/constants/routesConstants'
import {cleanEmptyFields} from '@/utils/objectUtils'
import {transformToLayeredPromotion} from '@/utils/promotions/promotions'
import {decodePromotionUrl, encodePromotionUrl} from '@/utils/urlUtils'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import {useCallback} from 'react'
import {useDispatch} from 'react-redux'
import {useNavigate} from 'react-router-dom'

export const usePromotionOperations = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Helper function to transform promotion data for API
  const transformPromotionForAPI = promotion => {
    // Create a deep copy to avoid mutating the original promotion object
    // This ensures the UI components continue to receive data in the expected format
    // while we transform the data for the API requirements
    const transformedPromotion = JSON.parse(JSON.stringify(promotion))

    // Encode banner URL if it exists
    if (transformedPromotion?.bannerUrl) {
      transformedPromotion.bannerUrl = encodePromotionUrl(transformedPromotion.bannerUrl)
    }

    // Remove fields not expected by the API
    if ('activeStartTime' in transformedPromotion) {
      delete transformedPromotion.activeStartTime
    }
    if ('activeEndTime' in transformedPromotion) {
      delete transformedPromotion.activeEndTime
    }

    // Since we now store the data in API format directly, we just need to ensure
    // the segments structure exists with proper defaults

    // // Ensure excludedSegments exists as an empty object if not present
    // if (!transformedPromotion.segments.excludedSegments) {
    //   transformedPromotion.segments.excludedSegments = {}
    // }

    // // Ensure selectedSegments is an array
    // if (!Array.isArray(transformedPromotion.segments.selectedSegments)) {
    //   transformedPromotion.segments.selectedSegments = []
    // }

    // // Build the segments object for API, preserving the exact structure
    // const segmentsForAPI = {
    //   type: transformedPromotion.segments.type ?? SegmentType.ALL_CUSTOMERS,
    //   selectedSegments: transformedPromotion.segments.selectedSegments || [],
    //   excludedSegments: transformedPromotion.segments.excludedSegments || {},
    // }

    // // Replace segments with API structure
    // transformedPromotion.segments = segmentsForAPI

    return transformedPromotion
  }

  // Mutation for creating/saving promotion as draft
  const {
    mutateAsync: draftMutateAsync,
    isPending: isDraftSaving,
    error: draftError,
  } = useMutation({
    mutationFn: async ({promotion}) => {
      const draftPromotion = {
        ...promotion,
        status: PromotionStatus.DRAFT,
      }
      const transformedPromotion = transformPromotionForAPI(draftPromotion)
      return await createPromotion(transformedPromotion)
    },
    onSuccess: (data, variables) => {
      const {options} = variables

      // Decode the banner URL in the response if it exists
      if (data?.data?.bannerUrl) {
        data.data.bannerUrl = decodePromotionUrl(data.data.bannerUrl)
      }

      if (options?.isSavingForImageUpload) {
        // We don't want to show toast message and redirect in case this draft save is for image upload
        return
      }
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Promotion saved as draft successfully',
          type: 'success',
        })
      )
      queryClient.invalidateQueries({queryKey: ['PROMOTION_DETAIL']})
      // Navigate to promotions list after successful save as draft
      navigate(ROUTES.PROMOTIONS_LIST)
    },
    onError: error => {
      console.error('Failed to save promotion as draft:', error)
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Failed to save promotion as draft',
          type: 'error',
        })
      )
    },
  })

  // Mutation for saving/publishing promotion
  const {
    mutateAsync: saveMutateAsync,
    isPending: isSaving,
    error: saveError,
  } = useMutation({
    mutationFn: async promotion => {
      const promotionToCreate = {
        ...promotion,
        status: PromotionStatus.PUBLISHED,
      }
      const transformedPromotion = transformPromotionForAPI(promotionToCreate)
      return await createPromotion(transformedPromotion)
    },
    onSuccess: data => {
      // Decode the banner URL in the response if it exists
      if (data?.data?.bannerUrl) {
        data.data.bannerUrl = decodePromotionUrl(data.data.bannerUrl)
      }

      dispatch(
        setToastNotifications({
          show: true,
          message: 'Promotion created successfully.',
          type: 'success',
        })
      )

      queryClient.invalidateQueries({queryKey: ['PROMOTION_DETAIL']})
      navigate(ROUTES.PROMOTIONS_LIST)
      setTimeout(() => {
        window.location.reload()
      }, 100)
    },
    onError: error => {
      console.error('Failed to create promotion:', error)
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Failed to create promotion.',
          type: 'error',
        })
      )
    },
  })

  const {
    mutateAsync: updateMutateAsync,
    isPending: isUpdating,
    error: updateError,
  } = useMutation({
    mutationFn: async ({promotion, isDraft = false, isCreating = false}) => {
      const promotionId = promotion?.id
      const promotionToUpdate = {
        ...promotion,
        status: isDraft ? PromotionStatus.DRAFT : PromotionStatus.PUBLISHED,
      }
      const transformedPromotion = transformPromotionForAPI(promotionToUpdate)
      return await updatePromotion(promotionId, transformedPromotion)
    },
    onSuccess: (data, variables, context) => {
      const {isCreating, isDraft} = variables

      // Decode the banner URL in the response if it exists
      if (data?.data?.bannerUrl) {
        data.data.bannerUrl = decodePromotionUrl(data.data.bannerUrl)
      }

      dispatch(
        setToastNotifications({
          show: true,
          message: isDraft
            ? 'Promotion saved as draft successfully.'
            : isCreating
              ? 'Promotion created successfully.'
              : 'Promotion updated successfully.',
          type: 'success',
        })
      )
      queryClient.invalidateQueries({queryKey: ['PROMOTION_DETAIL']})
      navigate(ROUTES.PROMOTIONS_LIST)
    },
    onError: (error, variables, context) => {
      const {isCreating, isDraft} = variables
      console.error(
        isDraft
          ? 'Failed to save promotion as draft:'
          : isCreating
            ? 'Failed to create promotion:'
            : 'Failed to update promotion:',
        error
      )
      dispatch(
        setToastNotifications({
          show: true,
          message: isDraft
            ? 'Failed to save promotion as draft.'
            : isCreating
              ? 'Failed to create promotion.'
              : 'Failed to update promotion.',
          type: 'error',
        })
      )
    },
  })

  const handleDraft = useCallback(
    async (promotion, options = {isSavingForImageUpload: false}) => {
      if (!promotion?.name || promotion?.name.trim() === '') {
        dispatch(
          setToastNotifications({
            show: true,
            message: 'Promotion Name is required to save as Draft',
            type: 'error',
          })
        )
        return
      }

      // Clean the promotion object by removing empty fields
      const cleanedPromotion = cleanEmptyFields(promotion)
      return await draftMutateAsync({promotion: transformToLayeredPromotion(cleanedPromotion), options})
    },
    [draftMutateAsync, dispatch]
  )

  const savePromotion = useCallback(
    async promotion => {
      // Clean the promotion object by removing empty fields (same as draft)
      const cleanedPromotion = cleanEmptyFields(promotion)
      return await saveMutateAsync(transformToLayeredPromotion(cleanedPromotion))
    },
    [saveMutateAsync]
  )

  const handleUpdatePromotion = useCallback(
    async (promotionData, {isDraft = false, isCreating = false} = {}) => {
      const cleanedPromotion = cleanEmptyFields(promotionData)

      return await updateMutateAsync({
        promotion: transformToLayeredPromotion(cleanedPromotion),
        isDraft,
        isCreating,
      })
    },

    [updateMutateAsync]
  )

  return {
    handleDraft,
    savePromotion,
    handleUpdatePromotion,
    isUpdating,
    updateError,
    isDraftSaving,
    isSaving,
    draftError,
    saveError,
  }
}
